// src/components/ImageUpload.tsx
import React, { FC, useState, useRef } from 'react';
import {
    Box,
    Button,
    IconButton,
    CircularProgress,
    Alert,
    Card,
    CardMedia,
    Typography,
} from '@mui/material';
import { CloudUpload, Delete, Image as ImageIcon } from '@mui/icons-material';
import {uploadApi} from "../../api/uploadApi.ts";

interface ImageUploadProps {
    value?: string;
    onChange: (url: string | undefined) => void;
    folder?: string;
    label?: string;
    helperText?: string;
    error?: boolean;
}

export const ImageUpload: FC<ImageUploadProps> = ({
                                                      value,
                                                      onChange,
                                                      folder = 'library',
                                                      label = '이미지 업로드',
                                                      helperText,
                                                      error,
                                                  }) => {
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string>();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // 파일 타입 검증
        if (!file.type.startsWith('image/')) {
            setUploadError('이미지 파일만 업로드 가능합니다.');
            return;
        }

        // 파일 크기 검증 (5MB)
        if (file.size > 5 * 1024 * 1024) {
            setUploadError('파일 크기는 5MB 이하여야 합니다.');
            return;
        }

        try {
            setUploading(true);
            setUploadError(undefined);

            // ✅ uploadApi 사용
            const response = await uploadApi.uploadImage(file, folder);
            onChange(response.fileUrl);
        } catch (error) {
            console.error('Upload error:', error);
            setUploadError('업로드 중 오류가 발생했습니다.');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDelete = async () => {
        if (value) {
            try {
                // ✅ 선택사항: 서버에서도 파일 삭제
                await uploadApi.deleteFile(value);
                onChange(undefined);
            } catch (error) {
                console.error('Delete error:', error);
                // 에러가 발생해도 UI에서는 제거
                onChange(undefined);
            }
        } else {
            onChange(undefined);
        }
    };

    return (
        <Box>
            <Typography variant="subtitle2" gutterBottom>
                {label}
            </Typography>

            {value ? (
                <Card sx={{ position: 'relative', maxWidth: 400 }}>
                    <CardMedia
                        component="img"
                        height="200"
                        image={value}
                        alt="Uploaded image"
                        sx={{ objectFit: 'contain', bgcolor: 'grey.100' }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            display: 'flex',
                            gap: 1,
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={handleDelete}
                            sx={{
                                bgcolor: 'background.paper',
                                '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' },
                            }}
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    </Box>
                </Card>
            ) : (
                <Box
                    sx={{
                        border: 2,
                        borderColor: error ? 'error.main' : 'divider',
                        borderStyle: 'dashed',
                        borderRadius: 1,
                        p: 3,
                        textAlign: 'center',
                        bgcolor: 'grey.50',
                        maxWidth: 400,
                    }}
                >
                    <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        이미지를 업로드하세요
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        sx={{ mt: 1 }}
                    >
                        {uploading ? '업로드 중...' : '파일 선택'}
                    </Button>
                </Box>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            {helperText && (
                <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 1, display: 'block' }}>
                    {helperText}
                </Typography>
            )}

            {uploadError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    {uploadError}
                </Alert>
            )}
        </Box>
    );
};
